// src/stores/banks.store.ts
import { create } from 'zustand'
import type { Bank } from '@/common/settings.common'
import { getBanks } from '@/api/settings.api'

type LoadArgs = {
  parentId: number
  includeInactive: boolean
}

type BanksStore = {
  banks: Bank[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  loadedKey: string | null

  ensureLoaded: (args: LoadArgs) => Promise<void>
  reload: (args: LoadArgs) => Promise<void>
  reset: () => void

  addBank: (bank: Bank) => void
  replaceBank: (bank: Bank) => void
  patchBankLocally: (id: number, patch: Partial<Bank> & Record<string, any>) => void
  removeBank: (id: number) => void
}

let inFlight: Record<string, Promise<void> | null> = {}

async function loadBanksIncrementally(args: LoadArgs, set: any, append: boolean) {
  const pageSize = 100
  let page = 1
  const seen = new Set<number>()

  if (!append) {
    set({
      banks: [],
      loading: true,
      loadingMore: false,
      error: null,
    })
  } else {
    set({
      loading: false,
      loadingMore: true,
      error: null,
    })
  }

  while (true) {
    const res = await getBanks({
      parentId: args.parentId,
      includeInactive: args.includeInactive,
      page,
      pageSize,
    })

    const items = (res.items ?? []) as Bank[]

    if (items.length === 0) break

    set((state: BanksStore) => {
      const next = page === 1 && !append ? [] : state.banks.slice()

      if (page === 1 && !append) {
        for (const b of items) {
          if (!seen.has(b.id)) {
            seen.add(b.id)
            next.push(b)
          }
        }
      } else {
        const existingIds = new Set(next.map(b => b.id))
        for (const b of items) {
          if (!existingIds.has(b.id) && !seen.has(b.id)) {
            seen.add(b.id)
            next.push(b)
          }
        }
      }

      return {
        banks: next,
        loading: false,
        loadingMore: true,
        error: null,
      }
    })

    if (items.length < pageSize) break
    page += 1
  }

  set({
    loading: false,
    loadingMore: false,
    error: null,
  })
}

export const useBanksStore = create<BanksStore>((set, get) => ({
  banks: [],
  loading: false,
  loadingMore: false,
  error: null,
  loadedKey: null,

  reset: () =>
    set({
      banks: [],
      loading: false,
      loadingMore: false,
      error: null,
      loadedKey: null,
    }),

  addBank: bank =>
    set(state => {
      const exists = state.banks.some(b => b.id === bank.id)
      if (exists) return state
      return { banks: [bank, ...state.banks] }
    }),

  replaceBank: bank =>
    set(state => ({
      banks: state.banks.map(b => (b.id === bank.id ? { ...b, ...bank } : b)),
    })),

  patchBankLocally: (id, patch) =>
    set(state => ({
      banks: state.banks.map(b => (b.id === id ? { ...b, ...patch } : b)),
    })),

  removeBank: id =>
    set(state => ({
      banks: state.banks.filter(b => b.id !== id),
    })),

  ensureLoaded: async args => {
    const key = `${args.parentId}:${args.includeInactive}`

    if (get().loadedKey === key && get().banks.length > 0) return
    if (inFlight[key]) return inFlight[key]!

    const p = (async () => {
      set({ loadedKey: key })
      try {
        await loadBanksIncrementally(args, set, false)
      } catch (e) {
        set({
          error: 'Fehler beim Laden der Banken.',
          loading: false,
          loadingMore: false,
        })
        throw e
      } finally {
        inFlight[key] = null
      }
    })()

    inFlight[key] = p
    return p
  },

  reload: async args => {
    const key = `${args.parentId}:${args.includeInactive}`

    if (inFlight[key]) return inFlight[key]!

    const p = (async () => {
      set({ loadedKey: key })
      try {
        await loadBanksIncrementally(args, set, false)
      } catch (e) {
        set({
          error: 'Fehler beim Laden der Banken.',
          loading: false,
          loadingMore: false,
        })
        throw e
      } finally {
        inFlight[key] = null
      }
    })()

    inFlight[key] = p
    return p
  },
}))
